import React from "react";
import { useTranslation } from "react-i18next";

import { UseCases } from "@planzone/core";
import { useEditAllocation, useRemoveAllocation } from "@planzone/view-models";
import { Safeguard } from "@scenes/safeguard";
import { ResourceAvatar } from "@common/resourceAvatar";
import { useModal } from "@root/client/modal";
import { Props } from "./types";
import { Completion } from "./completion";
import { AllocatedHours } from "./allocatedHours";
import { SpentHours } from "./spentHours";
import { BinIcon } from "./binIcon";
import { CompletionCheck } from "./completionCheck";
import styles from "./styles.scss";

function Allocation({
  allocation,
  isLastIncompleteAllocation,
  task,
  project,
}: Props) {
  const { isRequestPending, editAllocation } = useEditAllocation(allocation);
  const { removeAllocation, isRequestPending: isRemovalRequestPending } =
    useRemoveAllocation(allocation);
  const { pushView } = useModal();
  const { t } = useTranslation();

  return (
    <Safeguard
      useCases={[UseCases.editAllocation, UseCases.removeAllocation]}
      context={{ allocation }}
    >
      {({ canEditAllocation, canRemoveAllocation }) => (
        <div className={styles.allocation}>
          <div className={styles.resource}>
            <div
              className={styles.completionCheck}
              onClick={() => {
                isLastIncompleteAllocation
                  ? pushView({
                      key: UseCases.markTaskAsCompleted,
                      data: { task, project },
                    })
                  : editAllocation({
                      allocation,
                      nextAllocation: {
                        ...allocation,
                        percentComplete:
                          allocation.percentComplete !== 100 ? 100 : 0,
                      },
                    });
              }}
            >
              <CompletionCheck
                allocation={allocation}
                disabled={!canEditAllocation}
              />
            </div>
            <div className={styles.avatar}>
              <ResourceAvatar resource={allocation.resource} />
            </div>
            <div className={styles.user}>
              <p className={styles.name}>{allocation.resource.name}</p>
              {allocation.resource.profession && (
                <p className={styles.title}>{allocation.resource.profession}</p>
              )}
            </div>
          </div>
          {(!!allocation.allocatedHours || allocation.allocatedHours === 0) && (
            <div className={styles.container}>
              <AllocatedHours
                allocatedHours={allocation.allocatedHours}
                submit={(allocatedHours) =>
                  !isRequestPending &&
                  editAllocation({
                    allocation,
                    nextAllocation: { ...allocation, allocatedHours },
                  })
                }
                readOnly={!canEditAllocation}
              />
            </div>
          )}
          <div className={styles.container}>
            <SpentHours spentHours={allocation.spentHours ?? 0} />
          </div>
          <div>
            <div className={styles.container}>
              <Completion
                completion={allocation.percentComplete}
                submit={(percentComplete) =>
                  !isRequestPending &&
                  editAllocation({
                    allocation,
                    nextAllocation: { ...allocation, percentComplete },
                  })
                }
                readOnly={!canEditAllocation}
              />
            </div>
          </div>
          {canRemoveAllocation ? (
            <div
              className={styles.removeAllocation}
              onClick={(e) => {
                e.stopPropagation();
                !isRemovalRequestPending && removeAllocation(allocation);
              }}
            >
              <BinIcon className={styles.binIcon} />
            </div>
          ) : (
            <div className={styles.removeAllocation}>
              <div className={styles.binIcon} />
            </div>
          )}
        </div>
      )}
    </Safeguard>
  );
}

export { Allocation };
